import { useEffect, useMemo } from 'react'

import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import findTicketStore from 'entities/beket/models/find-ticket/find-ticket-store'
import formPassengersState from 'entities/beket/models/form-passengers/form-passengers-state'
import beketFormPassengersStore from 'entities/beket/models/form-passengers/form-passengers-state'
import {
  BeketPassengersValue,
  FormBeketPassengersValues,
} from 'entities/beket/models/form-passengers/model'
import beketListTicketsStore from 'entities/beket/models/ticket/beket-list-tickets/beket-list-tickets-store'
import { BeketOrderForm } from 'entities/beket/ui/order-form'
import { FormFeedback } from 'entities/beket/ui/order-form/ui/form-feedback'
import { FormFooter } from 'entities/beket/ui/order-form/ui/form-footer'
import { FormInfo } from 'entities/beket/ui/order-form/ui/form-info'
import { Account } from 'entities/user/module'
import userStore from 'entities/user/module/user-store'

import {
  BeketTransportType,
  TYPE_AGE_BEKET,
  TariffBeket,
} from 'shared/constants'
import { preparePhoneNumber } from 'shared/lib/prepare-phone-number'
import { HeaderGoBack } from 'shared/ui/header-go-back'

import s from './styles.module.scss'

interface FormInterface {
  adultCount?: number
  childCount?: number
  userData: Partial<Account> | null
  isDomestic?: boolean
}

const createDefaultValues = ({
  adultCount,
  childCount,
  userData,
  isDomestic,
}: FormInterface): FormBeketPassengersValues => {
  const adults: BeketPassengersValue[] = Array(adultCount).fill({
    tariff: TariffBeket.adult.key,
  })

  if (userData) {
    adults[0] = {
      ...adults[0],
      firstName: userData?.first_name || '',
      lastName: userData?.last_name || '',
    }

    if (!isDomestic) {
      adults[0] = {
        ...adults[0],
        serialDocuments: userData?.international_passport?.document_number,
        citizenship:
          userData?.international_passport?.citizenship.toUpperCase(),
      }
    }
  }

  const children: BeketPassengersValue[] = Array(childCount).fill({
    tariff: TariffBeket.child.key,
  })

  return {
    email: userData?.email || '',
    phone: preparePhoneNumber(userData?.phone_number),
    [TYPE_AGE_BEKET.ADULTS]: adults,
    [TYPE_AGE_BEKET.CHILDREN]: children,
  }
}

const BeketOrderPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { adult, child } = findTicketStore.getCountPassengers()

  const countPassengerLength = findTicketStore.getLengthOfPassengers()

  const selectedTicket = beketListTicketsStore.selectedTicket

  const methods = useForm<FormBeketPassengersValues>({
    defaultValues:
      beketFormPassengersStore.getFormPassengerStore() ||
      createDefaultValues({
        adultCount: adult,
        childCount: child,
        isDomestic: selectedTicket?.route?.is_domestic,
        userData: formPassengersState?.seatsResponse?.isForMySelf
          ? userStore?.account
          : null,
      }),
  })

  const handleSubmit = () => {
    navigate(RoutingPaths.beket.check.path())
  }

  const passengersChildren: BeketPassengersValue[] = useWatch({
    control: methods.control,
    name: 'child',
  })

  useEffect(() => {
    return () => {
      const passengerForm = methods.getValues()

      beketFormPassengersStore.setValuesForm(passengerForm)
    }
  }, [])

  const countriesList = useMemo(
    () =>
      beketFormPassengersStore.seatsResponse?.countries?.map((country) => ({
        name: country.name,
        value: String(country.id),
      })) || [],
    [],
  )

  const transportType =
    selectedTicket?.transport?.transport_model?.transport_type
  const isDomestic = Boolean(selectedTicket?.route?.is_domestic)
  let countPassenger = 0
  return (
    <div className={s.container}>
      <HeaderGoBack
        title={t('passenger_data')}
        buttonWhite
      />

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          className={s.form}
        >
          {[...new Array(adult)].map((_, indexAdult) => {
            return (
              <BeketOrderForm
                index={indexAdult}
                isAbroad={!isDomestic}
                citizenship={countriesList}
                typeAge={TYPE_AGE_BEKET.ADULTS}
                passengerCount={++countPassenger}
              />
            )
          })}

          {passengersChildren?.map((passenger, indexChild) => {
            return (
              <BeketOrderForm
                index={indexChild}
                isAbroad={!isDomestic}
                citizenship={countriesList}
                typeAge={TYPE_AGE_BEKET.CHILDREN}
                passengerCount={++countPassenger}
                currentIsChild={passenger?.tariff === TariffBeket.child.key}
                isChild={BeketTransportType.VAN.key !== transportType}
              />
            )
          })}

          <FormFeedback />

          <FormInfo transportType={transportType} />

          <FormFooter
            count={countPassengerLength}
            price={String(selectedTicket?.price)}
            bonus={selectedTicket?.mbonus?.amount}
          />
        </form>
      </FormProvider>
    </div>
  )
})

export default BeketOrderPage
